import type { CascadiaColor } from "@certn/cascadia-tokens";
import { useTranslation } from "react-i18next";

import {
  useLastRunStatusLabels,
  useMonitorStatusLabels,
  useRunResultLabels,
} from "./components/monitoringTags";

export const MONITOR_STATUS_VALUES = [
  "ACTIVE",
  "PAUSED",
  "ENDED",
  "DEACTIVATED",
  "NOT_ENROLLED",
] as const;

export type MonitorStatusValue = (typeof MONITOR_STATUS_VALUES)[number];

export const RUN_RESULT_VALUES = [
  "NO_CHANGE",
  "CHANGE_DETECTED",
  "UPCOMING_CHANGE",
  "REQUIRES_ATTENTION",
  "FAILED_TO_RUN",
] as const;

export type RunResultValue = (typeof RUN_RESULT_VALUES)[number];

export const LAST_RUN_STATUS_VALUES = ["SUCCESS", "FAILED", "PENDING"] as const;

export const ACTIVITY_LOG_LIFECYCLE_STATUS_VALUES = [
  "ENROLLED",
  "PAUSED",
  "RESUMED",
  "DEACTIVATED",
  "ENDED",
] as const;

export type ActivityLogLifecycleStatusValue =
  (typeof ACTIVITY_LOG_LIFECYCLE_STATUS_VALUES)[number];

export type MonitorStatusLabelKey =
  | MonitorStatusValue
  | ActivityLogLifecycleStatusValue;

export type LastRunStatusValue = (typeof LAST_RUN_STATUS_VALUES)[number];

export const SUMMARY_TILE_RESULT_VALUES = [
  "NO_CHANGE",
  "CHANGE_DETECTED",
  "REQUIRES_ATTENTION",
] as const;

export const NOTEWORTHY_RUN_RESULTS: ReadonlySet<RunResultValue> = new Set([
  "CHANGE_DETECTED",
  "UPCOMING_CHANGE",
  "REQUIRES_ATTENTION",
  "FAILED_TO_RUN",
]);

export function isNoteworthyRunResult(
  result: string
): result is RunResultValue {
  return NOTEWORTHY_RUN_RESULTS.has(result as RunResultValue);
}

export interface MonitoringPickerItem {
  id: string;
  name: string;
}

function toPickerItems<T extends string>(
  values: readonly T[],
  labels: Record<T, string>
): MonitoringPickerItem[] {
  return values.map((id) => ({ id, name: labels[id] }));
}

export function useMonitorStatusPickerItems(): MonitoringPickerItem[] {
  const labels = useMonitorStatusLabels();
  return toPickerItems(MONITOR_STATUS_VALUES, labels);
}

export function useMonitorStatusFilterPickerItems(): MonitoringPickerItem[] {
  const { t } = useTranslation();
  return [
    ...useMonitorStatusPickerItems(),
    { id: "ALL", name: t("All status") },
  ];
}

export function useRunResultPickerItems(): MonitoringPickerItem[] {
  const labels = useRunResultLabels();
  return toPickerItems(RUN_RESULT_VALUES, labels);
}

export function useLastRunStatusPickerItems(): MonitoringPickerItem[] {
  const labels = useLastRunStatusLabels();
  return toPickerItems(LAST_RUN_STATUS_VALUES, labels);
}

export function useActivityLogStatusPickerItems(): MonitoringPickerItem[] {
  const runLabels = useLastRunStatusLabels();
  const monitorLabels = useMonitorStatusLabels();
  return [
    ...toPickerItems(LAST_RUN_STATUS_VALUES, runLabels),
    ...ACTIVITY_LOG_LIFECYCLE_STATUS_VALUES.map((id) => ({
      id,
      name: monitorLabels[id],
    })),
  ];
}

interface SummaryTileStyle {
  labelColor: CascadiaColor;
  accent: string;
  background: string;
}

const RUN_RESULT_TILE_STYLES: Record<
  (typeof SUMMARY_TILE_RESULT_VALUES)[number],
  SummaryTileStyle
> = {
  NO_CHANGE: {
    labelColor: "text-accent-green-strong",
    accent: "var(--text-accent-green-strong)",
    background: "var(--background-accent-green-weak)",
  },
  CHANGE_DETECTED: {
    labelColor: "text-accent-orange-strong",
    accent: "var(--text-accent-orange-strong)",
    background: "var(--background-accent-orange-weak)",
  },
  REQUIRES_ATTENTION: {
    labelColor: "text-accent-red-strong",
    accent: "var(--text-accent-red-strong)",
    background: "var(--background-accent-red-weak)",
  },
};

export interface SummaryTileConfig extends SummaryTileStyle {
  result: (typeof SUMMARY_TILE_RESULT_VALUES)[number];
  label: string;
}

export function useSummaryTileConfigs(): SummaryTileConfig[] {
  const labels = useRunResultLabels();
  return SUMMARY_TILE_RESULT_VALUES.map((result) => ({
    result,
    label: labels[result],
    ...RUN_RESULT_TILE_STYLES[result],
  }));
}
